import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Lock, EyeOff, ExternalLink } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#051c14] text-gray-100 font-sans selection:bg-[#34d399] selection:text-black">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#051c14]/80 backdrop-blur-md px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft size={18} />
            <span>Back to Mahjong Blast</span>
          </Link>
          <div className="flex items-center gap-4 text-xs font-mono uppercase tracking-wider text-gray-400">
            <Link to="/terms" className="hover:text-white">Terms of Use</Link>
            <span>·</span>
            <a href="https://studio.corvas.in" target="_blank" rel="noopener noreferrer" className="hover:text-white flex items-center gap-1">
              Corvas Studio <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <section className="text-center py-6">
          <p className="text-xs font-mono tracking-widest uppercase text-[#34d399] mb-2">Legal &amp; Privacy Transparency</p>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-400">
            Application: <strong>Mahjong Blast: 3D Tile Match</strong> (<code>com.corvas.mahjong</code>)<br />
            Effective Date: March 1, 2026 · Developer: <strong>Corvas</strong>
          </p>
        </section>

        {/* Highlights */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#0b2c21] p-5">
            <div className="flex items-center gap-2 text-[#34d399] font-semibold text-sm mb-1">
              <ShieldCheck size={18} />
              <span>100% On-Device</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              Active recordings, gameplay, and media generation run on local device hardware without server uploads.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0b2c21] p-5">
            <div className="flex items-center gap-2 text-[#34d399] font-semibold text-sm mb-1">
              <Lock size={18} />
              <span>Zero Account Tracking</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              No login accounts, passwords, or personal profiles are required or collected by our team.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0b2c21] p-5">
            <div className="flex items-center gap-2 text-[#34d399] font-semibold text-sm mb-1">
              <EyeOff size={18} />
              <span>Safe Third Parties</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              We rely strictly on Google Play Services, AdMob, and Firebase for runtime stability and licensing.
            </p>
          </div>
        </div>

        {/* Section 1 */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">1. Overview &amp; Introduction</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            This Privacy Policy applies to the mobile software application <strong>Mahjong Blast: 3D Tile Match</strong> (the "Application"), developed and distributed by <strong>Corvas</strong> ("we," "our," or "Service Provider").
          </p>
          <p className="text-sm text-gray-300 leading-relaxed">
            We are committed to building high-performance, privacy-respecting software. This document discloses exactly what data the app interacts with, device permissions requested, and third-party services utilized.
          </p>
        </section>

        {/* Section 2 */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">2. Data Architecture &amp; Processing Boundaries</h2>
          
          <div>
            <h3 className="text-base font-semibold text-[#34d399] mb-1">A. Data Collected Directly by Developer</h3>
            <p className="text-sm text-gray-300 leading-relaxed">None. No personal profiles, passwords, or contact lists are collected.</p>
          </div>

          <div className="pt-2">
            <h3 className="text-base font-semibold text-[#34d399] mb-1">B. Data Processed Strictly Locally (Never Uploaded)</h3>
            <p className="text-sm text-gray-300 leading-relaxed">Player progression, cleared level milestones, high scores, daily login streaks, unlocked tile sets, and audio settings are saved locally on your device via Android shared preferences.</p>
          </div>

          <div className="pt-2">
            <h3 className="text-base font-semibold text-[#34d399] mb-1">C. Data Transmitted to Our Backend</h3>
            <p className="text-sm text-gray-300 leading-relaxed">None. All board configurations, physics, and tile matching logic execute on-device.</p>
          </div>

          <div className="pt-2">
            <h3 className="text-base font-semibold text-[#34d399] mb-1">D. Diagnostic Telemetry Collected by Third Parties</h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              Standard telemetry (anonymous crash logs, GPU capabilities, operating system version, and general ad impressions) is collected automatically by Google Play Services and AdMob according to their strict privacy standards.
            </p>
          </div>
        </section>

        {/* Permissions */}
        
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">3. Device Permissions &amp; Hardware Access</h2>
          <p className="text-sm text-gray-300">
            To provide core features, Mahjong Blast may request the following device permissions:
          </p>
          <div className="space-y-3 pt-2">
            
            <div className="rounded-xl border border-white/10 bg-black/30 p-4" key="Network Access">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <span className="font-semibold text-white text-base">Network Access</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-[#34d399]">Standard Internet Permission</span>
              </div>
              <p className="text-sm text-gray-300 mb-1"><strong>Purpose:</strong> Required to fetch daily challenge puzzles, verify in-app purchases, and load optional rewarded video ads.</p>
              <p className="text-sm text-gray-400"><strong>Data Handling:</strong> Transmits only anonymous game state verification tokens and ad requests.</p>
            </div>
  
          </div>
        </section>
        

        {/* Third-Party SDKs */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">4. Third-Party SDKs &amp; Service Providers</h2>
          <p className="text-sm text-gray-300">
            The Application integrates official third-party SDKs to support app distribution, licensing, analytics, and non-intrusive advertisements:
          </p>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-sm">
              <thead className="bg-black/40 text-xs font-semibold uppercase text-gray-400">
                <tr>
                  <th className="p-3">Service</th>
                  <th className="p-3">Purpose</th>
                  <th className="p-3">Privacy Link</th>
                </tr>
              </thead>
              <tbody>
                
                <tr className="border-b border-white/5" key="Google Play Games / Services">
                  <td className="p-3 font-semibold text-white whitespace-nowrap">Google Play Games / Services</td>
                  <td className="p-3 text-gray-300 text-sm">Facilitates optional cloud save backup, leaderboards, and achievements.</td>
                  <td className="p-3 text-sm">
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#34d399] hover:underline font-mono text-xs">
                      View Policy ↗
                    </a>
                  </td>
                </tr>
  

                <tr className="border-b border-white/5" key="Google AdMob">
                  <td className="p-3 font-semibold text-white whitespace-nowrap">Google AdMob</td>
                  <td className="p-3 text-gray-300 text-sm">Displays banner, interstitial, and voluntary rewarded video ads (for free power-up hints).</td>
                  <td className="p-3 text-sm">
                    <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-[#34d399] hover:underline font-mono text-xs">
                      View Policy ↗
                    </a>
                  </td>
                </tr>
  

                <tr className="border-b border-white/5" key="Google Play In-App Billing">
                  <td className="p-3 font-semibold text-white whitespace-nowrap">Google Play In-App Billing</td>
                  <td className="p-3 text-gray-300 text-sm">Handles digital purchases of coin packs, power-ups, and permanent ad-free upgrades.</td>
                  <td className="p-3 text-sm">
                    <a href="https://payments.google.com/payments/apis-secure/get_legal_document?ldo=0&amp;ldt=privacynotice" target="_blank" rel="noopener noreferrer" className="text-[#34d399] hover:underline font-mono text-xs">
                      View Policy ↗
                    </a>
                  </td>
                </tr>
  
              </tbody>
            </table>
          </div>
        </section>

        {/* Disclaimers */}
        
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">5. Product-Specific Clarifications &amp; Disclaimers</h2>
          <div className="space-y-4 pt-2">
            
          <div className="border-t border-white/5 pt-4 first:border-none first:pt-0" key="Virtual Goods &amp; Entertainment">
            <h4 className="text-base font-semibold text-[#34d399] mb-1">Virtual Goods &amp; Entertainment</h4>
            <p className="text-sm text-gray-300 leading-relaxed">Mahjong Blast is a skill-based puzzle game intended for entertainment purposes only. In-game power-ups, coins, and streak badges possess no cash value and do not constitute gambling or wagering.</p>
          </div>
  
          </div>
        </section>
        

        {/* Data Retention & Rights */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">6. Data Retention, User Control &amp; Children's Privacy</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            All user preferences and files reside strictly on your local device. You may purge data at any time via <em>Android Settings &gt; Apps &gt; Mahjong Blast &gt; Clear Storage</em>, or by uninstalling the application.
          </p>
          <p className="text-sm text-gray-300 leading-relaxed">
            We do not knowingly market to or solicit data from children under 13.
          </p>
        </section>

        {/* Contact */}
        <section className="rounded-2xl border border-white/10 bg-[#0b2c21] p-6 sm:p-8 space-y-3">
          <h2 className="text-xl font-bold text-white">7. Contact Information</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            If you have questions or concerns regarding this Privacy Policy, please contact us directly:
          </p>
          <p className="text-sm text-gray-300">
            <strong>Developer:</strong> Corvas<br />
            <strong>Email:</strong> <a href="mailto:corvas.official@gmail.com" className="text-[#34d399] hover:underline">corvas.official@gmail.com</a><br />
            <strong>Studio:</strong> <a href="https://studio.corvas.in" target="_blank" rel="noopener noreferrer" className="text-[#34d399] hover:underline">https://studio.corvas.in</a>
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 text-center text-xs text-gray-500">
        <div className="flex justify-center gap-4 mb-2">
          <Link to="/" className="hover:text-gray-300">Home</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-gray-300">Terms of Use</Link>
          <span>·</span>
          <a href="https://studio.corvas.in" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">Corvas Studio</a>
        </div>
        <p>&copy; {new Date().getFullYear()} Corvas. All rights reserved.</p>
      </footer>
    </div>
  );
}
